// Reads a plan out of the URL fragment and shows it, then offers to take it.
//
// Nothing here needs an account and nothing here is fetched. The whole plan
// arrives in the fragment, and the bill it names is already compiled into the
// application.

import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { decodePlan, tokenFromHash } from '../../engine/planlink.ts';
import { fmtRange } from '../../engine/time.ts';
import '../../styles/planning-refinements.css';

export function SharedPlan() {
  const { state, derived, actions } = useApp();
  const location = useLocation();
  const [taken, setTaken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const token = tokenFromHash(location.hash || (typeof window === 'undefined' ? '' : window.location.hash));

  const decoded = useMemo(() => {
    if (!state.ready || token === '') return null;
    return decodePlan(token, {
      days: state.days.map((d) => d.id),
      stages: state.stages.map((s) => s.id),
      sets: state.sets,
    });
  }, [token, state.ready, state.days, state.stages, state.sets]);

  const byDay = useMemo(() => {
    const groups = new Map<string, { dayLabel: string; rows: { id: string; act: string; stage: string; when: string; startTs: number }[] }>();
    for (const setId of decoded?.setIds ?? []) {
      const set = derived.setById.get(setId);
      if (!set) continue;
      const day = state.days.find((d) => d.id === set.dayId);
      if (!day) continue;
      const group = groups.get(day.id) ?? { dayLabel: day.label, rows: [] };
      group.rows.push({
        id: setId,
        act: derived.artistById.get(set.artistId)?.name ?? set.artistId,
        stage: derived.stageById.get(set.stageId)?.name ?? set.stageId,
        when: fmtRange(set.startTs, set.endTs),
        startTs: set.startTs,
      });
      groups.set(day.id, group);
    }
    for (const group of groups.values()) group.rows.sort((a, b) => a.startTs - b.startTs);
    return [...groups.entries()].sort(
      (a, b) => state.days.findIndex((d) => d.id === a[0]) - state.days.findIndex((d) => d.id === b[0]),
    );
  }, [decoded, derived.setById, derived.artistById, derived.stageById, state.days]);

  // Writes one pass per day, as the union of what is held and what arrived, so
  // taking a plan never drops a set the reader already chose.
  const take = async (replace: boolean) => {
    if (busy || !decoded) return;
    setBusy(true);
    setError('');
    setTaken('');
    try {
      for (const [dayId, group] of byDay) {
        const incoming = group.rows.map((r) => r.id);
        const held = replace
          ? []
          : state.picks.filter((p) => p.userId === state.userId && p.dayId === dayId).map((p) => p.setId);
        await actions.setDayPicks(dayId, [...new Set([...held, ...incoming])]);
      }
      setTaken(replace ? 'Replaced the included days in your plan.' : 'Added to your plan.');
    } catch {
      setError('Some changes could not be saved. Check your plan before trying again.');
    } finally {
      setBusy(false);
    }
  };

  if (!state.ready) return <p className="muted" role="status">Loading shared plan…</p>;

  if (token === '' || decoded === null) {
    return (
      <div className="panel planning-page planning-empty">
        <h1>Shared plan unavailable</h1>
        <p className="muted">
          This link is incomplete or cannot be read. Ask the sender to copy the full link again.
        </p>
        <Link to="/plan" className="buttonlink">Open my plan</Link>
      </div>
    );
  }

  const total = decoded.setIds.length;

  return (
    <div className="cols planning-page shared-plan-page">
      <div>
        <h1>Shared plan</h1>
        <p className="muted">
          {total === 0
            ? 'This link has no sets available in the current lineup.'
            : `${total} ${total === 1 ? 'set' : 'sets'} across ${byDay.length} ${byDay.length === 1 ? 'day' : 'days'}.`}
          {decoded.missing > 0 &&
            ` ${decoded.missing} ${decoded.missing === 1 ? 'set is' : 'sets are'} unavailable in the current lineup.`}
        </p>

        {byDay.map(([dayId, group]) => (
          <div className="panel" key={dayId}>
            <h3>{group.dayLabel}</h3>
            <div className="rowlist">
              {group.rows.map((row) => (
                <div className="row" key={row.id}>
                  <span>
                    {row.act}
                    <span className="sub" style={{ display: 'block' }}>
                      {row.stage}, {row.when}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="panel">
          <h3>Save to my plan</h3>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 16 }}>
            Add these sets alongside your saved picks.
          </p>
          <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="small" disabled={busy || total === 0} onClick={() => void take(false)}>
              {busy ? 'Saving…' : 'Add to my plan'}
            </button>
            <Link to="/plan" className="buttonlink">Open my plan</Link>
          </div>
          <details className="planning-replace">
            <summary>Replace saved days instead</summary>
            <p className="muted">This replaces all your picks on the {byDay.length === 1 ? 'day' : 'days'} included in this link.</p>
            <button
              type="button"
              className="small ghost"
              disabled={busy || total === 0}
              onClick={() => void take(true)}
            >
              Replace included days
            </button>
          </details>
          {error && <p role="alert" className="planning-error">{error}</p>}
          {taken !== '' && (
            <p className="muted" style={{ margin: '8px 0 0', fontSize: 16 }} role="status">
              {taken} <Link to="/plan">Open my plan</Link>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
